import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Flex, Heading, Link, List } from "@catchoftheday/cg-components";
import { styled, useTheme } from "@catchoftheday/theme";

import { MenuCategory } from "@header/components/MegaMenu/DesktopNav/MenuCategory";
import { TopBrands } from "@header/components/MegaMenu/DesktopNav/TopBrands";
import {
  ItemPath,
  trackMenuClick,
  trackMenuImpression,
} from "@header/components/MegaMenu/utilities/analytics";
import { Shop } from "@header/types";

import { CATEGORY_MENU_DESKTOP_WIDTH } from "../consts";

const NUM_COLUMNS = 5;

const NavPopout = styled(Flex)`
  transition: visibility 0s;
  width: 1120px;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    box-shadow: 20px 0px 40px -7px rgb(0 0 0 / 25%);
    z-index: -1;
  }

  @media (max-width: 1379px) {
    width: 1020px;
  }

  @media (max-width: 1279px) {
    width: 920px;
  }

  @media (max-width: 1179px) {
    width: 820px;
  }

  // last breakpoint before Tablet View (i.e. 1023px).
  @media (max-width: 1079px) {
    width: 720px;
  }
`;

const Column = styled(List)`
  columns: 150px;
  column-count: ${NUM_COLUMNS};
  column-gap: 28px;
`;

const ShopAllLink = styled(Link)`
  &:hover,
  &:focus,
  &:active {
    text-decoration: none;
  }
`;

type SubmenuPopoutProps = {
  shop: Shop;
  itemPathShop: ItemPath[];
  setParentFocus: () => void;
};

export const SubmenuPopover = ({
  shop,
  itemPathShop,
  setParentFocus,
}: SubmenuPopoutProps) => {
  const theme = useTheme();
  const hasSubmenuItems = shop.shopNavigationGroups.length > 0;
  const hasTopBrands = shop.topBrands.length > 0;
  const [focusItemIndex, setFocusItemIndex] = useState(0);
  const linkIds = useMemo<string[]>(() => [], []);

  useEffect(() => {
    linkIds.push("show-all-link");
    shop.shopNavigationGroups.map((navGroups) => {
      linkIds.push(`head-link-${navGroups.id}`);
      navGroups.shopNavigationItems.map((navItems) => {
        linkIds.push(`menu-link-${navItems.name}-${navItems.id}`);
      });
    });
    shop.topBrands.map((topBrand) => {
      linkIds.push(`menu-link-${topBrand.name}-${topBrand.id}`);
    });
  }, [linkIds, shop.shopNavigationGroups, shop.topBrands]);

  const keyDownHandler: React.KeyboardEventHandler<HTMLDivElement> =
    useCallback(
      (e) => {
        switch (e.code) {
          case "Home":
            e.stopPropagation();
            e.preventDefault();
            document.getElementById(`${linkIds[0]}`)?.focus();
            setFocusItemIndex(0);
            break;
          case "End":
            e.stopPropagation();
            e.preventDefault();
            document.getElementById(`${linkIds[linkIds.length - 1]}`)?.focus();
            setFocusItemIndex(linkIds.length - 1);
            break;
          case "ArrowRight":
            e.stopPropagation();
            break;
          case "ArrowUp":
            e.stopPropagation();
            e.preventDefault();
            if (focusItemIndex !== 0) {
              document
                .getElementById(`${linkIds[focusItemIndex - 1]}`)
                ?.focus();
              setFocusItemIndex(focusItemIndex - 1);
            }
            break;
          case "ArrowDown":
            e.stopPropagation();
            e.preventDefault();
            if (focusItemIndex !== linkIds.length - 1) {
              document
                .getElementById(`${linkIds[focusItemIndex + 1]}`)
                ?.focus();
              setFocusItemIndex(focusItemIndex + 1);
            }
            break;
          case "ArrowLeft":
          case "Escape":
            e.stopPropagation();
            setParentFocus();
            break;
          default:
            break;
        }
      },
      [linkIds, focusItemIndex, setParentFocus]
    );

  if (!hasSubmenuItems) {
    return null;
  }
  return (
    <NavPopout
      aria-label="Category Submenu"
      backgroundColor="white"
      m="auto"
      left={`${CATEGORY_MENU_DESKTOP_WIDTH}px`}
      maxWidth={theme.maxWidths.default}
      position="absolute"
      onKeyDown={keyDownHandler}
      onMouseEnter={() =>
        trackMenuImpression({
          name: shop.name,
          item_path: itemPathShop,
        })
      }
    >
      <Flex flexDirection="column" px="30px" pt="15px" pb="30px" width="80%">
        <Flex alignItems="flex-end">
          <Heading.h3 my="0px" pr="12px">
            {shop.name}
          </Heading.h3>
          <ShopAllLink
            tabIndex={-1}
            id={"show-all-link"}
            href={shop.url}
            fontSize="xs"
            fontWeight="400"
            pb="4px"
            hoverColor="secondary"
            onClick={() => {
              trackMenuClick({
                name: `Shop All ${shop.name}`,
                url: `${location.origin}${shop.url}`,
                item_path: [
                  ...itemPathShop,
                  {
                    name: `Shop All ${shop.name}`,
                    id: shop.id,
                    position: 0,
                  },
                ],
              });
            }}
          >
            Shop All
          </ShopAllLink>
        </Flex>
        <Flex flexDirection="row" position="relative" pt="25px" pb="32px">
          <Column pr="12px" m="0" width="100%" data-testid="nav-column">
            {shop.shopNavigationGroups.map((navItem, index) => (
              <MenuCategory
                key={navItem.id}
                navItem={navItem}
                itemPathCategory={[
                  ...itemPathShop,
                  {
                    name: navItem.name,
                    id: navItem.id,
                    position: index + 1,
                  },
                ]}
              />
            ))}
          </Column>
        </Flex>
      </Flex>
      {hasTopBrands && (
        <TopBrands
          topBrands={shop.topBrands}
          itemPathShop={itemPathShop}
          brandPosition={shop.shopNavigationGroups.length + 1}
        />
      )}
    </NavPopout>
  );
};
